import React, { memo, useContext, useState } from "react";

import { faFilter } from "@fortawesome/pro-light-svg-icons";
import { Checkbox, FormControlLabel, Popover } from "@material-ui/core";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import { setSetting } from "actions";
import FontAwesomeButton, {
  ButtonIcon,
} from "components/Button/FontAwesomeButton";
import { Row } from "components/Layout";
import { OptionToggler } from "components/OptionToggler";

import { Settings } from "constants/settings";

import { getSetting } from "selectors";

import { FiltersOptionsLayout } from "./Layout";
import {
  WeighBridgeSaleDispatchContext,
  WeighBridgeSaleStateContext,
} from "./WeighBridgeSaleContext";

const PenningFilterOptions = [
  {
    value: true,
    label: "Penned",
  },
  {
    value: false,
    label: "Not Penned",
  },
];

const InputModeOptions = [
  {
    value: false,
    label: "Tablet",
  },
  {
    value: true,
    label: "Keyboard",
  },
];

export function WeighBridgeFiltersComponent(props) {
  const { filters, keyboardModeEnabled, onChangeKeyboardMode } = props;

  const { updateFilters } = useContext(WeighBridgeSaleDispatchContext);

  const [filtersButtonEl, setFiltersButtonEl] = useState(null);

  const isFiltersVisible = Boolean(filtersButtonEl);

  function onChangePennedFilter(value) {
    updateFilters({ ...filters, penned: value });
  }

  function onChangeExactMatch(event) {
    updateFilters({ ...filters, exactMatch: event.target.checked });
  }

  function toggleFiltersVisible(event) {
    setFiltersButtonEl(isFiltersVisible ? null : event.target);
  }

  function closeFilters() {
    setFiltersButtonEl(null);
  }

  return (
    <Row>
      <FontAwesomeButton
        onClick={toggleFiltersVisible}
        inline
        size={2}
        padding={10}
        data-tour="filter"
      >
        <ButtonIcon icon={faFilter} />
      </FontAwesomeButton>

      <Popover
        open={isFiltersVisible}
        anchorEl={filtersButtonEl}
        onClose={closeFilters}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
      >
        <FiltersOptionsLayout>
          <OptionToggler
            name="Sale Lots Visible"
            value={filters.penned}
            onChange={onChangePennedFilter}
            options={PenningFilterOptions}
          />
          <OptionToggler
            name="Input Mode"
            value={keyboardModeEnabled}
            onChange={onChangeKeyboardMode}
            options={InputModeOptions}
          />
          <FormControlLabel
            style={{ marginTop: 12, display: "flex" }}
            control={
              <Checkbox
                checked={filters.exactMatch}
                onChange={onChangeExactMatch}
                color="primary"
                size="small"
              />
            }
            label="Exact pen match"
          />
        </FiltersOptionsLayout>
      </Popover>
    </Row>
  );
}

const WeighBridgeFilters = memo(
  WeighBridgeFiltersComponent,
  (a, b) =>
    a.filters.penned === b.filters.penned &&
    a.keyboardModeEnabled === b.keyboardModeEnabled &&
    a.filters.exactMatch === b.filters.exactMatch,
);
WeighBridgeFilters.propTypes = {
  filters: PropTypes.shape({
    penned: PropTypes.bool.isRequired,
    exactMatch: PropTypes.bool.isRequired,
  }),
  keyboardModeEnabled: PropTypes.bool,
  onChangeKeyboardMode: PropTypes.func,
};

function StateAdapter() {
  const { filters } = useContext(WeighBridgeSaleStateContext);
  const dispatch = useDispatch();
  const keyboardModeEnabled = useSelector(
    getSetting(Settings.weighbridgeKeyboardMode),
  );

  function onChangeKeyboardMode(value) {
    dispatch(setSetting(Settings.weighbridgeKeyboardMode, value));
  }

  return (
    <WeighBridgeFilters
      filters={filters}
      keyboardModeEnabled={!!keyboardModeEnabled}
      onChangeKeyboardMode={onChangeKeyboardMode}
    />
  );
}

export default memo(StateAdapter);
